import { useEffect, useState, useCallback } from "react";
import {
  Loader2, Car, Users, ShoppingCart, Sparkles, Gift, BarChart3, ChevronRight,
  Building2, Search, X, Info, CreditCard, Wallet, ArrowRightLeft, Layers, Receipt, ChevronLeft,
} from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { fmtTime } from "@/lib/datetime";

const INITIAL_CARDS = 6;
const REFRESH_MS = 30000;

export const fmtMoney = (n) =>
  "$" + Math.round(Number(n || 0)).toLocaleString("es-MX");

const ORIGEN_COLORS = {
  estacionamiento: "#3B82F6",
  valet: "#22C55E",
  productos: "#8B5CF6",
  servicios: "#F59E0B",
};
const METODO_COLORS = {
  Efectivo: "#22C55E",
  Tarjeta: "#3B82F6",
  Transferencia: "#8B5CF6",
  Mixto: "#F59E0B",
};
const MOTIVO_COLORS = ["#8B5CF6", "#22C55E", "#F59E0B", "#3B82F6", "#EF4444"];
const CAT_COLORS = {
  "Nómina y salarios": "#EF4444", "Insumos": "#F97316", "Mantenimiento": "#F59E0B",
  "Pago a proveedor": "#8B5CF6", "Otros": "#9CA3AF",
};
const metodoIcon = { Efectivo: Wallet, Tarjeta: CreditCard, Transferencia: ArrowRightLeft, Mixto: Layers };

/* Tarjeta de desglose por tipo de ingreso */
function DesgloseCard({ icon: Icon, label, monto, sub, muted, note, testid, onClick }) {
  const clickable = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      data-testid={testid}
      className={`text-left rounded-2xl border p-4 shadow-sm transition-all ${
        muted ? "border-dashed border-[#d4d4d8] bg-[#FAFAFA]" : "border-[#e4e4e7] bg-white"
      } ${clickable ? "hover:shadow-md hover:-translate-y-0.5 cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-center justify-between">
        <span className={`flex items-center gap-2 text-[11px] tracking-[0.1em] uppercase font-bold ${muted ? "text-[#A1A1AA]" : "text-[#52525B]"}`}>
          <Icon className={`h-4 w-4 ${muted ? "text-[#A1A1AA]" : "text-[#B45309]"}`} strokeWidth={2.3} /> {label}
        </span>
        {clickable && <ChevronRight className="h-4 w-4 text-[#A1A1AA]" />}
      </div>
      {muted ? (
        <p className="mt-3 text-xs text-[#A1A1AA] leading-snug">{note}</p>
      ) : (
        <>
          <p className="mt-2 font-display text-3xl font-black leading-none text-[#0A0A0A]" data-testid={testid ? `${testid}-monto` : undefined}>{fmtMoney(monto)}</p>
          {sub && <p className="mt-1.5 text-xs font-semibold text-[#71717A]">{sub}</p>}
        </>
      )}
    </button>
  );
}

/* Dona multi-segmento del origen de ingresos */
function DonutOrigen({ origen, total }) {
  const r = 52, c = 2 * Math.PI * r;
  let acc = 0;
  const segs = (origen || []).filter((o) => o.monto > 0);
  return (
    <div className="flex flex-col sm:flex-row items-center gap-6">
      <svg width="150" height="150" viewBox="0 0 150 150" className="shrink-0 -rotate-90">
        <circle cx="75" cy="75" r={r} fill="none" stroke="#EEF0F2" strokeWidth="16" />
        {segs.map((o) => {
          const len = (o.pct / 100) * c;
          const el = (
            <circle key={o.key} cx="75" cy="75" r={r} fill="none" stroke={ORIGEN_COLORS[o.key]} strokeWidth="16"
              strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-acc} />
          );
          acc += len;
          return el;
        })}
        <text x="75" y="70" textAnchor="middle" className="fill-[#0A0A0A] rotate-90" style={{ fontSize: 17, fontWeight: 800, transformOrigin: "center" }}>{fmtMoney(total)}</text>
        <text x="75" y="88" textAnchor="middle" className="fill-[#A1A1AA] rotate-90" style={{ fontSize: 10, transformOrigin: "center" }}>total</text>
      </svg>
      <div className="flex-1 w-full space-y-2">
        {(origen || []).map((o) => (
          <div key={o.key} className="flex items-center justify-between gap-2" data-testid={`origen-${o.key}`}>
            <span className="flex items-center gap-2 text-sm font-semibold text-[#3F3F46]">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: ORIGEN_COLORS[o.key] }} /> {o.label}
            </span>
            <span className="flex items-center gap-3">
              <span className="font-display font-black text-[#0A0A0A] tabular-nums">{fmtMoney(o.monto)}</span>
              <span className="text-xs font-bold text-[#A1A1AA] w-9 text-right tabular-nums">{o.pct}%</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Barras verticales de métodos de pago */
function MetodoBars({ metodos }) {
  const max = Math.max(1, ...(metodos || []).map((m) => m.monto));
  if (!metodos || metodos.length === 0) {
    return <p className="text-sm text-[#A1A1AA] py-8 text-center">Sin cobros registrados hoy.</p>;
  }
  return (
    <div className="flex items-end justify-around gap-4 h-[220px] pt-6">
      {metodos.map((m) => {
        const color = METODO_COLORS[m.metodo] || "#71717A";
        const h = Math.max(8, Math.round((m.monto / max) * 150));
        const Icon = metodoIcon[m.metodo] || Wallet;
        return (
          <div key={m.metodo} className="flex flex-col items-center gap-2 flex-1 max-w-[110px]" data-testid={`metodo-${m.metodo}`}>
            <span className="font-display text-sm font-black text-[#0A0A0A]">{fmtMoney(m.monto)}</span>
            <div className="w-full rounded-t-lg relative flex items-start justify-center" style={{ height: h, backgroundColor: color }}>
              <span className="mt-2 text-[11px] font-black text-white/90">{m.pct}%</span>
            </div>
            <span className="flex items-center gap-1 text-xs font-bold text-[#52525B]"><Icon className="h-3.5 w-3.5" /> {m.metodo}</span>
          </div>
        );
      })}
    </div>
  );
}

/* Tarjeta compacta por sucursal */
function SucIngresoCard({ s }) {
  return (
    <div data-testid={`ing-suc-${s.id_sucursal}`} className="rounded-2xl border border-[#e4e4e7] bg-white p-4 shadow-sm transition-all hover:shadow-md">
      <div className="flex items-center gap-2 mb-2">
        <Building2 className="h-4 w-4 text-[#3F3F46] shrink-0" />
        <p className="font-display font-black text-[#0A0A0A] truncate">{s.nombre}</p>
      </div>
      <p className="font-display text-2xl font-black text-[#0A0A0A]">{fmtMoney(s.monto)}</p>
      <div className="mt-2 flex items-center gap-4 text-xs text-[#71717A]">
        <span className="flex items-center gap-1"><Car className="h-3.5 w-3.5 text-[#B45309]" /> {s.tickets} tickets</span>
        <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5 text-[#B45309]" /> {s.servicios} servicios</span>
      </div>
    </div>
  );
}

export function IngresosContent({ data, onOpenCortesias, onOpenGastos }) {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(false);

  if (!data) {
    return <div className="flex justify-center py-24" data-testid="ingresos-loading"><Loader2 className="h-8 w-8 animate-spin text-[#B45309]" /></div>;
  }

  const suc = data.por_sucursal || [];
  const filtradas = suc.filter((s) => !search.trim() || s.nombre.toLowerCase().includes(search.trim().toLowerCase()));
  const visibles = expanded ? filtradas : suc.slice(0, INITIAL_CARDS);
  const restantes = suc.length - visibles.length;
  const cort = data.cortesias_motivos || [];

  return (
    <div className="space-y-6" data-testid="ingresos-content">
      {/* Resumen del día: Total cobrado · Gastos · Resultado operativo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-[#FCD34D] bg-gradient-to-r from-[#FEF9C3] to-[#FEF3C7] p-5 shadow-sm" data-testid="ingresos-total-card">
          <p className="flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase font-bold text-[#92400E]"><Wallet className="h-4 w-4" strokeWidth={2.4} /> Total cobrado hoy</p>
          <p className="font-display text-4xl font-black text-[#0A0A0A] leading-none mt-2" data-testid="ingresos-total-value">{fmtMoney(data.total_cobrado)}</p>
          <p className="mt-1 text-xs text-[#92400E]">Dinero efectivamente cobrado hoy.</p>
        </div>
        <button type="button" onClick={onOpenGastos} data-testid="ingresos-gastos-card"
          className="text-left rounded-2xl border border-[#FCA5A5] bg-[#FEF2F2] p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase font-bold text-[#B91C1C]"><Receipt className="h-4 w-4" strokeWidth={2.4} /> Gastos hoy</span>
            <ChevronRight className="h-4 w-4 text-[#FCA5A5]" />
          </div>
          <p className="font-display text-4xl font-black text-[#0A0A0A] leading-none mt-2" data-testid="ingresos-gastos-value">{fmtMoney(data.gastos_total)}</p>
          <p className="mt-1 text-xs text-[#B91C1C]">Gastos operativos registrados hoy.</p>
        </button>
        <div className="rounded-2xl border border-[#DDD6FE] bg-[#F5F3FF] p-5 shadow-sm" data-testid="ingresos-resultado-card">
          <p className="flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase font-bold text-[#6D28D9]"><BarChart3 className="h-4 w-4" strokeWidth={2.4} /> Resultado operativo hoy</p>
          <p className={`font-display text-4xl font-black leading-none mt-2 ${data.resultado_operativo >= 0 ? "text-[#0A0A0A]" : "text-[#DC2626]"}`} data-testid="ingresos-resultado-value">{fmtMoney(data.resultado_operativo)}</p>
          <p className="mt-1.5 text-xs font-bold text-[#6D28D9]" data-testid="ingresos-margen-value">Margen operativo: {data.total_cobrado > 0 ? `${(data.resultado_operativo / data.total_cobrado * 100).toFixed(1)}%` : "—"}</p>
          <p className="mt-1 text-[11px] text-[#A1A1AA]">Total cobrado − Gastos (las cortesías no se restan). Calculado con los gastos registrados en ParkNova.</p>
        </div>
      </div>

      {/* Desglose por tipo */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <DesgloseCard testid="ing-estacionamiento" icon={Car} label="Estacionamiento" monto={data.estacionamiento.monto} sub={`${data.estacionamiento.conteo} ${data.estacionamiento.conteo_label}`} />
        <DesgloseCard testid="ing-valet" icon={Users} label="Valet" monto={data.valet.monto} sub={`${data.valet.conteo} ${data.valet.conteo_label}`} />
        <DesgloseCard testid="ing-productos" icon={ShoppingCart} label="Productos" monto={data.productos.monto} sub={`${data.productos.conteo} ${data.productos.conteo_label}`} />
        <DesgloseCard testid="ing-servicios" icon={Sparkles} label="Servicios adicionales" monto={data.servicios.monto} sub={`${data.servicios.conteo} ${data.servicios.conteo_label}`} />
        <DesgloseCard testid="ing-recargas" icon={CreditCard} label="Recargas y pago de servicios" muted note={data.recargas?.nota || "Se mostrará cuando el módulo esté habilitado"} />
      </div>

      {/* Gastos de hoy por categoría */}
      <div className="rounded-2xl border border-[#e4e4e7] bg-white p-5 shadow-sm" data-testid="ingresos-gastos-categorias">
        <div className="flex items-center justify-between mb-1">
          <p className="flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase font-bold text-[#52525B]"><Receipt className="h-4 w-4 text-[#B45309]" /> Gastos de hoy por categoría</p>
          <button type="button" onClick={onOpenGastos} data-testid="ingresos-ver-gastos-btn" className="inline-flex items-center gap-1 text-xs font-bold text-[#2563EB] hover:underline">Ver detalle <ChevronRight className="h-3.5 w-3.5" /></button>
        </div>
        <p className="text-xs text-[#A1A1AA] mb-3">Distribución del total de gastos del día (no afecta ingresos ni cortesías).</p>
        {(data.gastos_categorias || []).length === 0 ? (
          <p className="text-sm text-[#A1A1AA] py-4">Sin gastos registrados hoy.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
            {data.gastos_categorias.map((c) => (
              <div key={c.categoria} className="flex items-center justify-between gap-2" data-testid={`ingresos-gasto-cat-${c.categoria}`}>
                <span className="flex items-center gap-2 text-sm text-[#3F3F46] truncate"><span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: CAT_COLORS[c.categoria] || "#9CA3AF" }} /> <span className="truncate">{c.categoria}</span></span>
                <span className="flex items-center gap-2 shrink-0"><span className="font-black text-[#0A0A0A] tabular-nums">{fmtMoney(c.monto)}</span><span className="text-xs font-bold text-[#A1A1AA] w-8 text-right">{c.pct}%</span></span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cortesías + Motivos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <button type="button" onClick={onOpenCortesias} data-testid="cortesias-card"
          className="text-left rounded-2xl border border-[#e4e4e7] bg-white p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="h-11 w-11 rounded-xl bg-[#EDE9FE] flex items-center justify-center shrink-0">
              <Gift className="h-5 w-5 text-[#7C3AED]" strokeWidth={2.3} />
            </div>
            <div>
              <p className="text-[11px] tracking-[0.12em] uppercase font-bold text-[#52525B]">Cortesías hoy</p>
              <p className="font-display text-3xl font-black text-[#0A0A0A] leading-none" data-testid="cortesias-count">{data.cortesias_total}</p>
              <p className="text-xs text-[#71717A]">operaciones · {fmtMoney(data.cortesias_monto)} en cortesías</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-[#A1A1AA]" />
        </button>

        <button type="button" onClick={onOpenCortesias} data-testid="motivos-card"
          className="text-left rounded-2xl border border-[#e4e4e7] bg-white p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
          <div className="flex items-center justify-between mb-3">
            <p className="flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase font-bold text-[#52525B]"><BarChart3 className="h-4 w-4 text-[#B45309]" /> Motivos de cortesía</p>
            <ChevronRight className="h-4 w-4 text-[#A1A1AA]" />
          </div>
          {cort.length === 0 ? (
            <p className="text-sm text-[#A1A1AA]">Sin cortesías hoy.</p>
          ) : (
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {cort.map((m, i) => (
                <div key={m.motivo} className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-sm text-[#3F3F46] truncate">
                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: MOTIVO_COLORS[i % MOTIVO_COLORS.length] }} />
                    <span className="truncate">{m.motivo}</span>
                  </span>
                  <span className="font-black text-[#0A0A0A] tabular-nums">{m.conteo}</span>
                </div>
              ))}
            </div>
          )}
        </button>
      </div>

      {/* Origen + Métodos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-[#e4e4e7] bg-white p-5 shadow-sm">
          <p className="text-[11px] tracking-[0.12em] uppercase font-bold text-[#52525B] mb-1">Origen de ingresos de hoy</p>
          <p className="text-xs text-[#A1A1AA] mb-4">Distribución del total cobrado.</p>
          <DonutOrigen origen={data.origen} total={data.total_cobrado} />
        </div>
        <div className="rounded-2xl border border-[#e4e4e7] bg-white p-5 shadow-sm">
          <p className="flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase font-bold text-[#52525B] mb-1"><BarChart3 className="h-4 w-4 text-[#B45309]" /> Métodos de pago de hoy</p>
          <p className="text-xs text-[#A1A1AA]">Montos cobrados por método de pago.</p>
          <MetodoBars metodos={data.metodos_pago} />
        </div>
      </div>

      {/* Ingresos por sucursal */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <div>
            <h2 className="font-display text-lg font-black text-[#0A0A0A] leading-none">Ingresos por sucursal</h2>
            <p className="text-xs text-[#A1A1AA] mt-1">Total cobrado hoy en cada sucursal.</p>
          </div>
          {expanded && (
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A1A1AA]" />
              <Input data-testid="ing-suc-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar sucursal…" className="pl-9 bg-white" />
            </div>
          )}
        </div>
        {visibles.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-[#d4d4d8] bg-white p-8 text-center text-sm text-[#A1A1AA]">Sin ingresos que mostrar.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4" data-testid="ingresos-sucursales-grid">
            {visibles.map((s) => <SucIngresoCard key={s.id_sucursal} s={s} />)}
          </div>
        )}
        {!expanded && restantes > 0 && (
          <button data-testid="ing-ver-todas-btn" onClick={() => setExpanded(true)}
            className="mt-4 inline-flex items-center gap-2 rounded-lg border border-[#e4e4e7] bg-white px-4 py-2 text-sm font-bold text-[#3F3F46] hover:bg-[#FAFAFA] transition-colors">
            Ver todas las sucursales ({suc.length}) <ChevronRight className="h-4 w-4" />
          </button>
        )}
        {expanded && (
          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs text-[#A1A1AA]">{filtradas.length} de {suc.length} sucursales</span>
            <button data-testid="ing-ver-menos-btn" onClick={() => { setExpanded(false); setSearch(""); }}
              className="inline-flex items-center gap-1 text-sm font-bold text-[#52525B] hover:text-[#0A0A0A]"><X className="h-4 w-4" /> Ver menos</button>
          </div>
        )}
      </div>
    </div>
  );
}

const fmtHora = (iso) => {
  if (!iso) return "—";
  return fmtTime(iso);
};

export function GastosHoyPanel({ open, onOpenChange, filtro }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const cargar = useCallback(async (p) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filtro && filtro !== "all") qs.set("id_sucursal", filtro);
      qs.set("preset", "hoy"); qs.set("page", String(p)); qs.set("page_size", "10");
      const { data } = await api.get(`/dashboard/historial/gastos?${qs.toString()}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filtro]);

  useEffect(() => { if (open) { setPage(1); cargar(1); } }, [open, cargar]);
  const go = (p) => { setPage(p); cargar(p); };

  const items = data?.items || [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const desde = (page - 1) * (data?.page_size || 10) + 1;
  const hasta = data ? Math.min(page * data.page_size, data.total) : 0;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="ingresos-gastos-panel">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#e4e4e7]">
          <SheetTitle className="flex items-center gap-2 font-display text-xl font-black text-[#0A0A0A]">
            <Receipt className="h-5 w-5 text-[#DC2626]" /> Detalle de gastos de hoy
          </SheetTitle>
          <SheetDescription>Gastos operativos registrados durante el día.</SheetDescription>
        </SheetHeader>
        <div className="p-6 space-y-5">
          <div className="rounded-xl border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 flex items-start gap-2" data-testid="ingresos-gastos-panel-resumen">
            <Info className="h-4 w-4 text-[#DC2626] mt-0.5 shrink-0" />
            {data ? (
              <p className="text-sm font-bold text-[#B91C1C]">{data.total} gasto(s) registrado(s) hoy.</p>
            ) : (
              <div className="flex items-center gap-2 text-sm text-[#B91C1C]"><Loader2 className="h-4 w-4 animate-spin" /> Cargando gastos…</div>
            )}
          </div>
          {loading && !data ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#B45309]" /></div>
          ) : items.length === 0 ? (
            <p className="text-center text-sm text-[#A1A1AA] py-12">No hay gastos registrados hoy.</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-[#e4e4e7]">
              <table className="w-full text-sm" data-testid="ingresos-gastos-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-[#A1A1AA] bg-[#FAFAFA] border-b border-[#e4e4e7]">
                    <th className="px-3 py-2 font-bold">Sucursal</th>
                    <th className="px-3 py-2 font-bold">Hora</th>
                    <th className="px-3 py-2 font-bold">Categoría</th>
                    <th className="px-3 py-2 font-bold">Concepto</th>
                    <th className="px-3 py-2 font-bold text-right">Importe</th>
                    <th className="px-3 py-2 font-bold">Usuario</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} data-testid={`ingresos-gasto-row-${it.id}`} className="border-b border-[#f4f4f5] last:border-0">
                      <td className="px-3 py-2.5 font-semibold text-[#3F3F46]">{it.sucursal}</td>
                      <td className="px-3 py-2.5 text-[#52525B] tabular-nums">{fmtHora(it.fecha)}</td>
                      <td className="px-3 py-2.5 text-[#3F3F46]">{it.categoria}</td>
                      <td className="px-3 py-2.5 text-[#3F3F46]">{it.concepto || "—"}</td>
                      <td className="px-3 py-2.5 text-right font-bold text-[#0A0A0A] tabular-nums">{fmtMoney(it.monto)}</td>
                      <td className="px-3 py-2.5 text-[#52525B]">{it.usuario || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data && data.total > data.page_size && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#A1A1AA]" data-testid="ingresos-gastos-page">{desde}–{hasta} de {data.total}</span>
              <div className="flex items-center gap-1">
                <button data-testid="ingresos-gastos-prev" disabled={page <= 1} onClick={() => go(page - 1)}
                  className="inline-flex items-center gap-1 rounded-lg border border-[#e4e4e7] bg-white px-3 py-1.5 text-sm font-bold text-[#3F3F46] hover:bg-[#FAFAFA] disabled:opacity-40 transition-colors"><ChevronLeft className="h-4 w-4" /> Anterior</button>
                <span className="px-2 text-sm font-bold text-[#52525B]">{page} / {totalPages}</span>
                <button data-testid="ingresos-gastos-next" disabled={page >= totalPages} onClick={() => go(page + 1)}
                  className="inline-flex items-center gap-1 rounded-lg border border-[#e4e4e7] bg-white px-3 py-1.5 text-sm font-bold text-[#3F3F46] hover:bg-[#FAFAFA] disabled:opacity-40 transition-colors">Siguiente <ChevronRight className="h-4 w-4" /></button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function CortesiasPanel({ open, onOpenChange, filtro }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtro && filtro !== "all") params.set("id_sucursal", filtro);
      const { data } = await api.get(`/dashboard/cortesias?${params.toString()}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filtro]);

  useEffect(() => { if (open) cargar(); }, [open, cargar]);
  useEffect(() => {
    if (!open) return;
    const id = setInterval(cargar, REFRESH_MS);
    return () => clearInterval(id);
  }, [open, cargar]);

  const items = data?.items || [];
  const motivos = data?.motivos || [];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="cortesias-panel">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#e4e4e7]">
          <SheetTitle className="flex items-center gap-2 font-display text-xl font-black text-[#0A0A0A]">
            <Gift className="h-5 w-5 text-[#7C3AED]" /> Detalle de cortesías
          </SheetTitle>
          <SheetDescription>Cortesías aplicadas durante el día.</SheetDescription>
        </SheetHeader>
        <div className="p-6 space-y-5">
          <div className="rounded-xl border border-[#BFDBFE] bg-[#EFF6FF] px-4 py-3 flex items-start gap-2" data-testid="cortesias-resumen">
            <Info className="h-4 w-4 text-[#2563EB] mt-0.5 shrink-0" />
            {data ? (
              <div>
                <p className="text-sm font-bold text-[#1E3A8A]">Total de cortesías: {data.total}</p>
                <p className="text-xs text-[#3B82F6]">{fmtMoney(data.monto)} en cortesías (no se suman al total cobrado).</p>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-[#1E3A8A]"><Loader2 className="h-4 w-4 animate-spin" /> Cargando cortesías…</div>
            )}
          </div>

          {motivos.length > 0 && (
            <div>
              <p className="text-[11px] tracking-[0.12em] uppercase font-bold text-[#52525B] mb-2">Cortesías por motivo</p>
              <div className="space-y-1.5">
                {motivos.map((m, i) => (
                  <div key={m.motivo} className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm text-[#3F3F46]">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: MOTIVO_COLORS[i % MOTIVO_COLORS.length] }} /> {m.motivo}
                    </span>
                    <span className="font-black text-[#0A0A0A] tabular-nums">{m.conteo}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {loading && !data ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#B45309]" /></div>
          ) : items.length === 0 ? (
            <p className="text-center text-sm text-[#A1A1AA] py-12">No hay cortesías registradas hoy.</p>
          ) : (
            <div className="overflow-hidden rounded-xl border border-[#e4e4e7]">
              <table className="w-full text-sm" data-testid="cortesias-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-[#A1A1AA] bg-[#FAFAFA] border-b border-[#e4e4e7]">
                    <th className="px-3 py-2 font-bold">Sucursal</th>
                    <th className="px-3 py-2 font-bold">Hora</th>
                    <th className="px-3 py-2 font-bold">Folio</th>
                    <th className="px-3 py-2 font-bold">Tipo</th>
                    <th className="px-3 py-2 font-bold">Motivo</th>
                    <th className="px-3 py-2 font-bold text-right">Importe</th>
                    <th className="px-3 py-2 font-bold">Usuario</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id_ticket} data-testid={`cortesia-row-${it.id_ticket}`} className="border-b border-[#f4f4f5] last:border-0">
                      <td className="px-3 py-2.5 font-semibold text-[#3F3F46]">{it.sucursal}</td>
                      <td className="px-3 py-2.5 text-[#52525B] tabular-nums">{fmtHora(it.hora)}</td>
                      <td className="px-3 py-2.5 font-mono text-[#52525B]">{it.folio || "—"}</td>
                      <td className="px-3 py-2.5 text-[#3F3F46]">{it.tipo}</td>
                      <td className="px-3 py-2.5 text-[#3F3F46]">{it.motivo || "—"}</td>
                      <td className="px-3 py-2.5 text-right font-bold text-[#0A0A0A] tabular-nums">{fmtMoney(it.importe)}</td>
                      <td className="px-3 py-2.5 text-[#52525B]">{it.usuario || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
